import { useAuth } from '../contexts/AuthContext'
import { MatterType } from './matterService'
import { API_BASE_URL } from '../config'

export interface ClientInvite {
  id: string;
  tenantId: string;
  status: 'invited' | 'submitted';
  matterType?: MatterType | null;
  createdAt: string;
  expiresAt: string;
  clientId?: string;
  matterId?: string | null;
  completedAt?: string;
}

export interface CreateInvitePayload {
  matterType?: MatterType;
}

// Authenticated: only a logged-in tenant can mint an invite link.
export const useInviteService = () => {
  const { token, authFetch } = useAuth()

  const createInvite = async (payload: CreateInvitePayload = {}): Promise<ClientInvite> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/client-invites`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to create invite: ${response.status}`)
    }

    return data.invite
  }

  return { createInvite }
}

// Public: the invited client never logs in, so these do not attach a bearer
// token — the unguessable invite id in the URL is the only credential.

export interface InviteStatus {
  valid: boolean;
  matterType?: MatterType | null;
}

export const getInviteStatus = async (inviteId: string): Promise<InviteStatus> => {
  const response = await fetch(`${API_BASE_URL}/client-invites/${inviteId}`)
  const data = await response.json()

  if (!response.ok) {
    throw new Error(data?.error || `This link is not valid (${response.status})`)
  }

  return data
}

export interface SubmitClientInvitePayload {
  firstName: string;
  lastName: string;
  emailAddress: string;
  mobileNumber: string;
  companyName?: string;
  streetAddress: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  source: {
    sourceType: 'bank-account' | 'credit-card';
    bankAccountName?: string;
    bankAccountBsb?: string;
    bankAccountNumber?: string;
  };
  // Only meaningful when the invite was generated with a matterType — the
  // client supplies these themselves since the tenant doesn't know them yet.
  matterPropertyAddress?: string;
  matterSettlementDate?: string;
}

export const submitClientInvite = async (
  inviteId: string,
  payload: SubmitClientInvitePayload,
): Promise<{ success: boolean; firstName: string }> => {
  const response = await fetch(`${API_BASE_URL}/client-invites/${inviteId}/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const data = await response.json()

  if (!response.ok) {
    throw new Error(data?.error || `Failed to submit your details (${response.status})`)
  }

  return data
}
